import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Pencil, Plus, RefreshCw } from 'lucide-react';
import api from '../../../services/api';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import {
  PortfolioStrategy,
  PORTFOLIO_TYPES,
  biddingTypeLabel,
  googleStatusLabel,
  parseDecimal,
  portfolioTargetKind,
  portfolioTargetText,
} from './rmfLabels';

interface Props {
  campaignId: string;
  /** resource_name da estratégia de portfólio que a campanha usa hoje (se usar). */
  currentPortfolio?: string | null;
  portfolios: PortfolioStrategy[];
  loading: boolean;
  reload: () => Promise<void>;
  onAssigned: () => void;
  onCancel: () => void;
}

const errMsg = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback;

/** Converte a meta digitada na tela (CPA em R$ ou ROAS em %) no valor da API (moeda ou razão). */
const toApiTarget = (type: string, raw: string): number | null => {
  const n = parseDecimal(raw);
  if (isNaN(n) || n <= 0) return null;
  return portfolioTargetKind(type) === 'roas' ? n / 100 : n;
};

/** Valor da meta para preencher o campo de edição. */
const toInputTarget = (p: PortfolioStrategy): string => {
  if (portfolioTargetKind(p.type) === 'roas') return p.target_roas != null ? String(Math.round(Number(p.target_roas) * 100)) : '';
  return p.target_cpa != null ? Number(p.target_cpa).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
};

const GooglePortfolioBidding: React.FC<Props> = ({ campaignId, currentPortfolio, portfolios, loading, reload, onAssigned, onCancel }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(currentPortfolio || '');
  const [assigning, setAssigning] = useState(false);

  // Criar nova
  const [creating, setCreating] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<string>('MAXIMIZE_CONVERSIONS');
  const [newTarget, setNewTarget] = useState('');

  // Editar a selecionada
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editName, setEditName] = useState('');
  const [editTarget, setEditTarget] = useState('');

  const usable = useMemo(() => portfolios.filter((p) => String(p.status).toUpperCase() !== 'REMOVED'), [portfolios]);
  const current = usable.find((p) => p.resource_name === selected) || null;

  useEffect(() => {
    if (!selected && usable.length > 0) setSelected(currentPortfolio && usable.some((p) => p.resource_name === currentPortfolio) ? currentPortfolio : usable[0].resource_name);
  }, [usable, currentPortfolio, selected]);

  const typeOptions = PORTFOLIO_TYPES.map((v) => ({ value: v, label: biddingTypeLabel(t, v) }));
  const targetLabel = (type: string) => {
    const optional = type === 'MAXIMIZE_CONVERSIONS' || type === 'MAXIMIZE_CONVERSION_VALUE';
    const base = portfolioTargetKind(type) === 'roas'
      ? t('campaigns.rmf.portfolioTargetRoas', 'ROAS alvo (%)')
      : t('campaigns.rmf.portfolioTargetCpa', 'CPA alvo (R$)');
    return optional ? `${base} ${t('campaigns.rmf.optional', '(opcional)')}` : base;
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error(t('campaigns.rmf.portfolioNameRequired', 'Dê um nome à estratégia'));
      return;
    }
    const target = toApiTarget(newType, newTarget);
    if ((newType === 'TARGET_CPA' || newType === 'TARGET_ROAS') && target == null) {
      toast.error(t('campaigns.rmf.portfolioTargetRequired', 'Informe a meta da estratégia'));
      return;
    }
    setSavingNew(true);
    try {
      const res = await api.post('/campaigns/google/portfolio-strategies', { name: newName.trim(), type: newType, target });
      toast.success(t('campaigns.rmf.portfolioCreated', 'Estratégia de portfólio criada no Google Ads'));
      await reload();
      const rn = res.data?.data?.resource_name;
      if (rn) setSelected(rn);
      setCreating(false);
      setNewName('');
      setNewTarget('');
    } catch (err: any) {
      toast.error(errMsg(err, t('campaigns.rmf.portfolioCreateError', 'Falha ao criar a estratégia')));
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = () => {
    if (!current) return;
    setEditName(current.name || '');
    setEditTarget(toInputTarget(current));
    setEditing(true);
    setCreating(false);
  };

  const handleSaveEdit = async () => {
    if (!current) return;
    setSavingEdit(true);
    try {
      await api.put(`/campaigns/google/portfolio-strategies/${current.id}`, {
        name: editName.trim() || undefined,
        type: current.type,
        target: toApiTarget(current.type, editTarget),
      });
      toast.success(t('campaigns.rmf.portfolioUpdated', 'Estratégia de portfólio atualizada'));
      setEditing(false);
      await reload();
    } catch (err: any) {
      toast.error(errMsg(err, t('campaigns.rmf.portfolioUpdateError', 'Falha ao atualizar a estratégia')));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAssign = async () => {
    if (!current) return;
    if (!window.confirm(t('campaigns.rmf.portfolioAssignConfirm', 'A campanha passa a usar a estratégia "{{name}}" no Google Ads. Continuar?', { name: current.name }))) return;
    setAssigning(true);
    try {
      await api.post(`/campaigns/${campaignId}/google-bidding`, { portfolio: current.resource_name });
      toast.success(t('campaigns.rmf.portfolioAssigned', 'Campanha agora usa a estratégia de portfólio'));
      onAssigned();
    } catch (err: any) {
      toast.error(errMsg(err, t('campaigns.rmf.portfolioAssignError', 'Falha ao aplicar a estratégia')));
    } finally {
      setAssigning(false);
    }
  };

  const busy = assigning || savingNew || savingEdit;

  return (
    <div className="space-y-3">
      {loading && usable.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('campaigns.rmf.portfolioLoading', 'Carregando estratégias de portfólio...')}
        </div>
      ) : usable.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.rmf.portfolioEmpty', 'Nenhuma estratégia de portfólio nesta conta. Crie uma abaixo.')}
        </p>
      ) : (
        <>
          <div className="flex items-end gap-2">
            <Select
              label={t('campaigns.rmf.portfolioSelect', 'Estratégia de portfólio')}
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setEditing(false); }}
              disabled={busy}
              wrapperClassName="flex-1 min-w-0"
              options={usable.map((p) => ({
                value: p.resource_name,
                label: `${p.name} · ${biddingTypeLabel(t, p.type)} · ${portfolioTargetText(p)} · ${t('campaigns.rmf.portfolioCampaigns', '{{n}} campanha(s)', { n: p.campaigns })}`,
              }))}
            />
            <button
              type="button"
              onClick={() => reload()}
              disabled={loading}
              className="mb-1 p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              title={t('campaigns.rmf.refresh', 'Atualizar')}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {current && !editing && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-sm grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('campaigns.rmf.type', 'Tipo')}</div>
                <div className="font-medium text-gray-900 dark:text-white">{biddingTypeLabel(t, current.type)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('campaigns.rmf.target', 'Meta')}</div>
                <div className="font-medium text-gray-900 dark:text-white">{portfolioTargetText(current)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('campaigns.rmf.campaigns', 'Campanhas')}</div>
                <div className="font-medium text-gray-900 dark:text-white">{current.campaigns}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('campaigns.rmf.status', 'Status')}</div>
                <div className="font-medium text-gray-900 dark:text-white">{googleStatusLabel(t, current.status)}</div>
              </div>
              <div className="col-span-2 sm:col-span-4">
                <button
                  type="button"
                  onClick={startEdit}
                  disabled={busy}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 disabled:opacity-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {t('campaigns.rmf.portfolioEdit', 'Editar esta estratégia de portfólio')}
                </button>
              </div>
            </div>
          )}

          {current && editing && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t('campaigns.rmf.portfolioEditWarn', 'Mudar esta estratégia afeta todas as {{n}} campanha(s) que a usam.', { n: current.campaigns })}
              </p>
              <Input
                label={t('campaigns.rmf.portfolioName', 'Nome')}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={savingEdit}
              />
              <Input
                type="text"
                inputMode="decimal"
                label={targetLabel(current.type)}
                value={editTarget}
                onChange={(e) => setEditTarget(e.target.value)}
                disabled={savingEdit}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSaveEdit} isLoading={savingEdit} disabled={savingEdit}>
                  {t('campaigns.rmf.portfolioSaveEdit', 'Salvar estratégia')}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={savingEdit}>
                  {t('campaigns.rmf.cancel', 'Cancelar')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Nova estratégia de portfólio */}
      {!creating ? (
        <button
          type="button"
          onClick={() => { setCreating(true); setEditing(false); }}
          disabled={busy}
          className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1 disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('campaigns.rmf.portfolioNew', 'Criar nova estratégia de portfólio')}
        </button>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 space-y-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {t('campaigns.rmf.portfolioNewTitle', 'Nova estratégia de portfólio')}
          </p>
          <Input
            label={t('campaigns.rmf.portfolioName', 'Nome')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={savingNew}
          />
          <Select
            label={t('campaigns.rmf.type', 'Tipo')}
            value={newType}
            onChange={(e) => { setNewType(e.target.value); setNewTarget(''); }}
            disabled={savingNew}
            options={typeOptions}
          />
          <Input
            type="text"
            inputMode="decimal"
            label={targetLabel(newType)}
            placeholder={portfolioTargetKind(newType) === 'roas' ? '400' : '0,00'}
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
            disabled={savingNew}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleCreate} isLoading={savingNew} disabled={savingNew}>
              {t('campaigns.rmf.portfolioCreate', 'Criar estratégia')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setCreating(false)} disabled={savingNew}>
              {t('campaigns.rmf.cancel', 'Cancelar')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button size="sm" onClick={handleAssign} isLoading={assigning} disabled={busy || !current}>
          {t('campaigns.rmf.portfolioAssign', 'Usar nesta campanha')}
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={busy}>
          {t('campaigns.rmf.cancel', 'Cancelar')}
        </Button>
      </div>
    </div>
  );
};

export default GooglePortfolioBidding;
